import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * useColumnPrefs(tableKey, allColumns)
 *
 * Reusable hook for tables that need user-configurable columns (show/hide +
 * drag-reorder). Preferences are persisted to localStorage under
 * "tablePrefs:<tableKey>" so each user's choices stick across reloads.
 *
 * @param {string} tableKey - Stable unique key per table, e.g. "inventory".
 * @param {Array<{key: string, label: string, defaultVisible?: boolean, locked?: boolean}>} allColumns
 *        - `locked` columns can't be reordered or hidden (e.g. row select / actions).
 * @returns {{
 *   visibleColumns: typeof allColumns,
 *   orderedColumns: typeof allColumns,
 *   isVisible: (key:string) => boolean,
 *   toggleColumn: (key:string) => void,
 *   moveColumn: (fromKey:string, toKey:string) => void,
 *   reset: () => void,
 * }}
 */
export function useColumnPrefs(tableKey, allColumns) {
  const storageKey = `tablePrefs:${tableKey}`;

  // Defaults derived from allColumns
  const defaults = useMemo(() => ({
    order: allColumns.map((c) => c.key),
    hidden: allColumns.filter((c) => c.defaultVisible === false).map((c) => c.key),
  }), [allColumns]);

  const [prefs, setPrefs] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return defaults;
      const parsed = JSON.parse(raw);
      // Ignore any stored keys that no longer exist in allColumns; append any
      // newly-added columns to the end of the stored order.
      const allKeys = allColumns.map((c) => c.key);
      const cleanOrder = [
        ...parsed.order.filter((k) => allKeys.includes(k)),
        ...allKeys.filter((k) => !parsed.order.includes(k)),
      ];
      return {
        order: cleanOrder,
        hidden: (parsed.hidden || []).filter((k) => allKeys.includes(k)),
      };
    } catch {
      return defaults;
    }
  });

  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(prefs)); } catch { /* ignore */ }
  }, [storageKey, prefs]);

  const colByKey = useMemo(() => {
    const map = {};
    for (const c of allColumns) map[c.key] = c;
    return map;
  }, [allColumns]);

  const orderedColumns = useMemo(
    () => prefs.order.map((k) => colByKey[k]).filter(Boolean),
    [prefs.order, colByKey],
  );

  const visibleColumns = useMemo(
    () => orderedColumns.filter((c) => c.locked || !prefs.hidden.includes(c.key)),
    [orderedColumns, prefs.hidden],
  );

  const isVisible = useCallback(
    (key) => colByKey[key]?.locked || !prefs.hidden.includes(key),
    [colByKey, prefs.hidden],
  );

  const toggleColumn = useCallback((key) => {
    if (colByKey[key]?.locked) return;
    setPrefs((p) => {
      const hidden = p.hidden.includes(key)
        ? p.hidden.filter((k) => k !== key)
        : [...p.hidden, key];
      return { ...p, hidden };
    });
  }, [colByKey]);

  const moveColumn = useCallback((fromKey, toKey) => {
    if (!fromKey || !toKey || fromKey === toKey) return;
    if (colByKey[fromKey]?.locked || colByKey[toKey]?.locked) return;
    setPrefs((p) => {
      const order = [...p.order];
      const fromIdx = order.indexOf(fromKey);
      const toIdx = order.indexOf(toKey);
      if (fromIdx < 0 || toIdx < 0) return p;
      order.splice(fromIdx, 1);
      order.splice(toIdx, 0, fromKey);
      return { ...p, order };
    });
  }, [colByKey]);

  const reset = useCallback(() => setPrefs(defaults), [defaults]);

  return { visibleColumns, orderedColumns, isVisible, toggleColumn, moveColumn, reset };
}
